import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-hot-toast";

import { ReactComponent as PlayIcon } from "components/icons/play.svg";
import { ReactComponent as LogoImage } from "components/icons/logo-full.svg";
import { IMenuItem, ShareGifMenu } from "components/menus";
import { copyGifLink, sendGifToReddit, sendGifToTwitter } from "utils/share";
import { EmbedGifDialog } from "components/dialogs";
import { ProgressBar } from "components/player/ProgressBar";
import { useFullScreen } from "hooks/useFullScreen";
import { metrics, ViewRegion } from "../../utils/metrics";

import { UserInfo } from "./UserInfo";
import { VideoButtons } from "./VideoButtons";
import { Actions } from "./types";

import "./styles/common.scss";
import "./styles/video.scss";

const FULL = 100;

export const EmbeddedVideoPlayer = (props: {
  id: string;
  createDate: number;
  userName?: string;
  userPic?: string;
  sdVideo: string;
  hdVideo?: string;
  poster: string;
  sound: boolean;
  verified: boolean;
}): JSX.Element => {
  const [isPlaying, setPlaying] = useState<boolean>(true);
  const [isMuted, setMuted] = useState<boolean>(true);
  const [isHD, setHD] = useState<boolean>(false);
  const [showShare, setShowShare] = useState<boolean>(false);
  const [showEmbed, setShowEmbed] = useState<boolean>(false);

  // Video progress tracking.
  const [buffered, setBuffered] = useState<number>(0);
  const [current, setCurrent] = useState<number>(0);
  const [seek, setSeek] = useState<number>(0);

  const ref = useRef<HTMLVideoElement>(null);
  const docRef = useRef<HTMLDivElement>(null);

  const { inFullscreenMode, toggleFullscreen, fullscreenEnabled } = useFullScreen({ ref: docRef });

  // Pass the new seek position to the video component.
  const onSeek = useCallback((value: number) => {
    setSeek(value);
  }, []);

  const onProgress = useCallback((buffered: number, current: number) => {
    setCurrent(current);
    setBuffered(buffered);
  }, []);

  const handleProgress = useCallback(() => {
    const em = ref.current;

    if (em) {
      if (em.buffered.length > 0) {
        const buf = em.buffered.end(0);
        const buf_pc = buf * FULL / em.duration;
        const cur_pc = em.currentTime * FULL / em.duration;

        onProgress(buf_pc, cur_pc);
      }
    }
  }, [onProgress]);

  // Seek to the right position but only when it changes.
  useEffect(() => {
    const em = ref.current;

    if (seek && em) {
      const time: number = em.duration * seek / FULL;
      if (Number.isFinite(time)) {
        em.currentTime = time;
      }
    }
  }, [seek, ref]);

  const onClick = () => {
    if (ref.current) {
      if (ref.current.paused) {
        const playPromise = ref.current.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setPlaying(true);
            })
            .catch((e) => {
              console.warn("failed to play video", e);
            });
        } else {
          setPlaying(true);
        }
      } else {
        ref.current.pause();
        setPlaying(false);
      }
    }
  };

  const restart = () => {
    if (ref.current) {
      ref.current.load();
    }
  };

  const onAction = (action: Actions) => {
    if (action === Actions.Mute) {
      setMuted(true);
      console.debug("Sound disabled.");
    } else if (action === Actions.UnMute) {
      setMuted(false);
      console.debug("Sound enabled.");
    } else if (action === Actions.FullScreen) {
      toggleFullscreen(null);
    } else if (action === Actions.HD) {
      setHD(true);
      console.debug("Switch to HD.");
      restart();
    } else if (action === Actions.SD) {
      setHD(false);
      console.debug("Switch to SD.");
      restart();
    } else if (action === Actions.Share) {
      setShowShare(true);
      ref.current && ref.current.pause();
      setPlaying(false);
      console.debug("Open share dialog.");
    } else {
      console.debug(`Unhandled action: ${action}`);
    }
  };

  const onShareClick = (item: IMenuItem) => {
    setShowShare(false);

    if (item.id === "link" || item.id === "discord") {
      console.debug("Copy gif link.");
      copyGifLink(props.id);
      toast.success("Link copied!");
    } else if (item.id === "embed") {
      console.debug("Embed link.");
      setShowEmbed(true);
    } else if (item.id === "reddit") {
      console.debug("Send to Reddit.");
      sendGifToReddit(props.id);
    } else if (item.id === "twitter") {
      console.debug("Send to Twitter.");
      sendGifToTwitter(props.id);
    } else {
      console.debug("Share menu item clicked.", item);
    }
  };

  const onShareClose = () => {
    console.debug("Closing the share menu.");
    setShowShare(false);
  };

  const onCloseEmbed = () => {
    setShowEmbed(false);
  };

  const sendMetrics = useCallback(() => {
    metrics.sendGifView({
      gifId: props.id,
      tags: [],
      fullscreen: false,
      source: ViewRegion.Embed,
      position: 1,
    });
  }, [props.id]);

  const handlePlay = () => {
    sendMetrics();
  };

  return (
    <div className="embeddedPlayer" ref={docRef}>
      <video
        ref={ref}
        controls={false}
        autoPlay={true}
        loop={true}
        playsInline
        poster={props.poster}
        muted={isMuted}
        disablePictureInPicture={true}
        onClick={onClick}
        onProgress={handleProgress}
        onDurationChange={handleProgress}
        onTimeUpdate={handleProgress}
        onPlay={handlePlay}
        src={isHD ? props.hdVideo : props.sdVideo}
      />

      <a
        href="https://www.redgifs.com/"
        target="_blank"
        title="Open RedGIFs home page"
        className="logo"
        rel="noreferrer"
      >
        <LogoImage />
      </a>

      <UserInfo
        gifId={props.id}
        createDate={props.createDate}
        name={props.userName}
        userpic={props.userPic}
        verified={props.verified}
      />

      <VideoButtons
        hasSound={props.sound}
        muted={isMuted}
        hd={isHD}
        haveFullScreen={fullscreenEnabled}
        fullScreen={inFullscreenMode}
        onClick={onAction}
      />

      {!isPlaying && (
        <PlayIcon
          className="playIcon"
          onClick={onClick}
        />
      )}

      {showShare && (
        <ShareGifMenu
          onClick={onShareClick}
          onClose={onShareClose}
        />
      )}

      {showEmbed && (
        <EmbedGifDialog
          id={props.id}
          onClose={onCloseEmbed}
        />
      )}

      <ProgressBar
        buffered={buffered}
        current={current}
        onSeek={onSeek}
      />
    </div>
  );
};
